/**
 * WAN uptime from UniFi Site Manager (`statistics.percentages.wanUptime`).
 *
 * This is how long the internet circuit has been up, not how many access
 * points happen to be powered on. UniFi has returned the figure both as a
 * 0–100 percentage and as a 0–1 fraction; both become a percentage here.
 */

export function normalizeWanUptime(value: number | string | null | undefined): number | null {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n == null || !Number.isFinite(n)) return null;
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  if (pct < 0 || pct > 100) return null;
  return pct;
}

/** Worst reported circuit. A second WAN at 80% must not disappear into a 99% average. */
export function worstWanUptime(values: Array<number | string | null | undefined>): number | null {
  let worst: number | null = null;
  for (const value of values) {
    const pct = normalizeWanUptime(value);
    if (pct == null) continue;
    worst = worst == null ? pct : Math.min(worst, pct);
  }
  return worst;
}

export function averageWanUptime(values: Array<number | string | null | undefined>): number | null {
  let sum = 0;
  let count = 0;
  for (const value of values) {
    const pct = normalizeWanUptime(value);
    if (pct == null) continue;
    sum += pct;
    count += 1;
  }
  return count > 0 ? sum / count : null;
}

/** One decimal, so 99.84% reads as 99.8% and a full circuit reads as 100%. */
export function formatWanUptime(value: number | string | null | undefined): string | null {
  const pct = normalizeWanUptime(value);
  if (pct == null) return null;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded >= 100) return '100%';
  return `${rounded.toFixed(1)}%`;
}
